/** @jsx jsx */

import { useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import { Text } from '@chameleoncreator/creator-ui';
import 'services/ie11closestPolyfill';
import bulletStyles from './bulletStyles';
import { TextToSpeech } from './TextToSpeech';
import { useSelector } from 'react-redux';
import { State } from 'store/store';
import { getScoreDataFromScoringBlocks } from 'services/getScoreDataFromScoringBlocks';
import { getSCORMInitialized } from 'components/lib/Scorm/SCORM';

const ReadOnlyText = ({
  id,
  content,
  className,
  // useBackgroundControls,
  borderRadius,
  // backgroundColor,
  background,
  bulletColor,
  liTextColor,
  linkColors,
  enableTextToSpeechBtn,
  ...props
}) => {
  const textToSpeech = useSelector((state: State) => state.theme.textToSpeech);
  //
  const moduleVariables = useSelector((state: State) => state.moduleVariables);

  const blocks = useSelector((state: State) => state.blocks);
  const rootBlockId = useSelector((state: State) => state.rootBlockId);
  const userBehaviour = useSelector((state: State) => state.userBehaviour);

  const readOnlyText = useRef(null);
  const fontInfo = useRef<{ fontSize: number; lineHeight: number } | undefined>(
    undefined
  );

  let padding;

  if (background) {
    padding = `${12.5 + borderRadius}px ${10 + borderRadius}px`;
  }

  if (!!borderRadius) {
    padding = borderRadius * 3;
  }

  useEffect(() => {
    if (!readOnlyText.current) {
      return;
    }

    const varElems = Array.from(
      (readOnlyText.current as HTMLDivElement).querySelectorAll(
        '[data-type="variable"]'
      )
    );

    if (!varElems?.length) {
      return;
    }

    varElems.forEach(vE => {
      const varName = vE.getAttribute('data-varname');
      const { childNodes } = vE;

      if (!varName) {
        return;
      }

      if (varName === 'DATE') {
        vE.textContent = Date()
          .toString()
          .split(/ /g)
          .slice(0, 4)
          .join(' ');
        return;
      }

      if (varName === 'SCORE') {
        const score =
          getScoreDataFromScoringBlocks({
            blocks,
            rootBlockId,
            userBehaviour,
          })?.score || 0;

        console.log(`SCORE: ${score}%`);

        vE.textContent = `${score}%`;
        return;
      }

      // if (
      //   new URLSearchParams(new URL(window.location.href).search).get(
      //     'testModuleVariables'
      //   ) === '1'
      // ) {
      //   vE.textContent = `Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.`;
      //   return;
      // }

      if (childNodes?.length) {
        childNodes[childNodes.length - 1].textContent =
          moduleVariables[varName];
        return;
      }

      vE.textContent = moduleVariables[varName];
    });
  });

  useEffect(() => {
    if (!readOnlyText.current) {
      return;
    }

    const span = (readOnlyText.current as HTMLDivElement).querySelector(
      '[data-type="fontSize"]'
    ) as HTMLSpanElement;

    if (span) {
      fontInfo.current = {
        fontSize: Number(span.style.fontSize.replace(/[^\d.-]+/g, '')),
        lineHeight: Number(span.style.lineHeight.replace(/[^\d.-]+/g, '')),
      };
    }

    const links: HTMLAnchorElement[] = Array.from(
      (readOnlyText.current as HTMLDivElement).querySelectorAll('a')
    );

    links.forEach(link => {
      //
      if (link?.href.includes('mailto:') && getSCORMInitialized()) {
        link.target = '_blank';
      }
    });
    //
  }, []);

  return (
    <Text
      ref={readOnlyText}
      className={`chameleon-text${className ? ` ${className}` : ''}`}
      as="div"
      css={css`
        a:link {
          color: ${linkColors.link} !important;
        }

        a:visited {
          color: ${linkColors.visited} !important;
        }

        a:hover {
          color: ${linkColors.hover} !important;
        }

        a:focus-visible {
          color: ${linkColors.focus} !important;

          outline-offset: 2px !important;
          outline: 1px solid ${linkColors.focus} !important;
          text-decoration: none !important;
        }

        a:active {
          color: ${linkColors.active} !important;
        }

        span[data-type='variable'] {
          white-space: pre-wrap !important;
        }

        ${bulletStyles(bulletColor, liTextColor)}

        ${fontInfo.current &&
          !props.certificate &&
          `
        @media(max-width: ${360 / window.devicePixelRatio}px) {
          span {
            font-size: ${fontInfo.current?.fontSize * 1.6}vw !important;
            line-height: ${fontInfo.current?.lineHeight / 8}vw !important;      
          }
        }
        `}
      `}
      style={
        background
          ? {
              borderRadius,
              background,
              padding: `${12.5 + borderRadius}px ${10 + borderRadius}px`,
            }
          : {}
      }
    >
      <div
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />

      {'speechSynthesis' in window &&
        textToSpeech &&
        enableTextToSpeechBtn &&
        content && (
          <TextToSpeech id={`text-to-speech-${id}`} content={content} />
        )}
    </Text>
  );
};

export default ReadOnlyText;
